/**
 * Office locations — shared across OrgChart, Office Directory, and map-centered components.
 * The Firestore collection `offices` holds the mutable source of truth; these constants
 * are used as seeds and as a fallback when Firestore data is unavailable.
 */

export const DEFAULT_OFFICE_ID = 'new-orleans';

export const OFFICES = {
  'new-orleans': {
    id: 'new-orleans',
    name: 'New Orleans',
    address: '2917 Magazine St',
    city: 'New Orleans',
    state: 'LA',
    zip: '70115',
    phone: '(504) 300-0700',
    email: 'info@reverealtors.com',
    hours: 'Mon–Fri 9am–5pm',
    latitude: 29.9250,
    longitude: -90.0850,
    timezone: 'America/Chicago',
    color: { bg: '#6366F1', text: '#fff' }, // indigo
  },
  'covington': {
    id: 'covington',
    name: 'Covington',
    address: '501 N Hwy 190, Suite 4',
    city: 'Covington',
    state: 'LA',
    zip: '70433',
    phone: '(985) 888-0700',
    email: 'covington@reverealtors.com',
    hours: 'Mon–Fri 9am–5pm',
    latitude: 30.4755,
    longitude: -90.1009,
    timezone: 'America/Chicago',
    color: { bg: '#14B8A6', text: '#fff' }, // teal
  },
  'baton-rouge': {
    id: 'baton-rouge',
    name: 'Baton Rouge',
    address: '7575 Jefferson Hwy',
    city: 'Baton Rouge',
    state: 'LA',
    zip: '70806',
    phone: '(225) 888-0700',
    email: 'batonrouge@reverealtors.com',
    hours: 'Mon–Fri 9am–5pm',
    latitude: 30.4515,
    longitude: -91.1871,
    timezone: 'America/Chicago',
    color: { bg: '#EE8172', text: '#fff' }, // coral
  },
};

/** Lookup an office's center coordinates, falling back to New Orleans. */
export function getOfficeCenter(officeId) {
  const o = OFFICES[officeId] || OFFICES[DEFAULT_OFFICE_ID];
  return { lat: o.latitude, lng: o.longitude };
}

/**
 * officeCodeFromId — short display code for an office id slug (RW-1534-A:
 * the deal-number prefix, "NO-26-014"). Pure derivation so every surface
 * agrees without a lookup: multi-word slugs use initials (new-orleans → NO,
 * baton-rouge → BR), single words the first three letters (covington → COV).
 * Null-safe: returns null for missing/unknown-shaped ids.
 */
export function officeCodeFromId(officeId) {
  if (typeof officeId !== 'string' || !officeId.trim()) return null;
  const parts = officeId.trim().toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
  if (!parts.length) return null;
  const code = parts.length > 1
    ? parts.map((w) => w[0]).join('')
    : parts[0].slice(0, 3);
  return code.toUpperCase();
}

/**
 * RW-3409 — one display name per office, everywhere. Offices reached screens
 * as ids ("new-orleans" on the Roster, Onboarding and Settings), as Darwin's
 * names ("Prytania Street", "Northshore" in Reports) and as older labels
 * ("Northshore" in Recruiting, "Covington-Northshore"). Clint 10-20: the names
 * are the offices collection's — New Orleans, Baton Rouge, Covington.
 *
 * Takes an office id or any name an office has gone by; returns its display
 * name, or the value as given when it is no office we know ('' for nothing).
 * Pure.
 */
const OFFICE_ALIASES = {
  'new orleans': 'new-orleans', 'prytania': 'new-orleans', 'prytania street': 'new-orleans', 'prytania st': 'new-orleans',
  'baton rouge': 'baton-rouge',
  'covington': 'covington', 'northshore': 'covington', 'north shore': 'covington',
  'covington-northshore': 'covington', 'covington northshore': 'covington', 'covington (northshore)': 'covington',
};

/** The office id an id or name refers to, or null. */
export function officeIdOf(value) {
  if (value == null) return null;
  const v = String(value).trim().toLowerCase();
  if (!v) return null;
  if (OFFICES[v]) return v;
  return OFFICE_ALIASES[v] || OFFICE_ALIASES[v.replace(/[-_]+/g, ' ')] || null;
}

export function officeDisplayName(value) {
  const id = officeIdOf(value);
  if (id) return OFFICES[id].name;
  return value == null ? '' : String(value).trim();
}

/** Display names of the offices, in the constants' order — for pickers and filters. */
export const OFFICE_NAMES = Object.values(OFFICES).map((o) => o.name);

/**
 * RW-3504 — the offices a free-text place or title points at (Events' office
 * filters). Calendar locations name the office by street ("4827 Prytania St."),
 * not city, and titles carry a code prefix ("BR Rêve | Realtors Team Meeting"),
 * so a city-name match found nothing. Matches each office's name, city,
 * address number + street, its RW-3409 aliases, and its short code. The codes
 * are upper-case whole words only — "BR"/"COV" in a title, never "br" inside
 * text — and New Orleans' "NO" is left out (it is a word). Pure; returns ids.
 */
const OFFICE_TEXT_CODES = { 'new-orleans': [/\bnola\b/i], 'baton-rouge': [/\bBR\b/], covington: [/\bCOV\b/] };
const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export function officeIdsInText(text) {
  const t = String(text || '');
  if (!t.trim()) return [];
  const lower = t.toLowerCase();
  const has = (phrase) => !!phrase && new RegExp(`(^|[^a-z0-9])${escapeRe(phrase)}($|[^a-z0-9])`).test(lower);
  return Object.values(OFFICES).filter((o) => {
    const street = (String(o.address || '').toLowerCase().match(/^\d+\s+[a-z]+/) || [])[0];
    const aliases = Object.keys(OFFICE_ALIASES).filter((a) => OFFICE_ALIASES[a] === o.id);
    return [o.name.toLowerCase(), String(o.city || '').toLowerCase(), street, ...aliases].some(has)
      || (OFFICE_TEXT_CODES[o.id] || []).some((re) => re.test(t));
  }).map((o) => o.id);
}
